import { AccountMenuComponent } from './shared/components/account-menu/account-menu.component';
import { AsideService } from '@my-backoffice/app/pages/main/shared/components/aside/shared/services/aside.service';
import { HeaderService } from '@my-backoffice/app/pages/main/shared/components/header/shared/service/header.service';
import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { AUTHENTICATION_SERVICE } from '@my-backoffice/app/shared/auth/authentication.token';
import { TranslateMenuComponent } from '@my-backoffice/app/shared/components/translate-menu/translate-menu.component';
import { IMAGES } from '@my-backoffice/app/shared/constants/images.constants';
import { QimaNullableType } from '@my-backoffice/app/shared/models/types/qima.type';
import { QimaButtonService } from '@my-backoffice/app/shared/services/exported-services/qima-button/qima-button.service';
import { QimaButtonIconService } from '@my-backoffice/app/shared/services/exported-services/qima-button-icon/qima-button-icon.service';
import { QimaDividerService } from '@my-backoffice/app/shared/services/exported-services/qima-divider/qima-divider.service';
import { QimaIconService } from '@my-backoffice/app/shared/services/exported-services/qima-icon/qima-icon.service';
import { QimaTooltipService } from '@my-backoffice/app/shared/services/exported-services/qima-tooltip/qima-tooltip.service';
import { AppcuesService } from '@my-backoffice/app/shared/services/third-party-services/appcues/appcues.service';
import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { EQimaButtonIconStyle, EQimaPosition, QimaButtonIconModule, QimaTooltipDirectiveModule, QimaDividerModule, QimaIconModule } from '@qima/ngx-qima';
import { Observable, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    QimaButtonIconModule,
    QimaTooltipDirectiveModule,
    CdkOverlayOrigin,
    QimaDividerModule,
    QimaIconModule,
    CdkConnectedOverlay,
    AccountMenuComponent,
    TranslateMenuComponent,
    TranslateModule,
  ],
})
export class HeaderComponent implements OnInit {
  /**
   * @description
   * Controls whether the aside toggle button is visible.
   * @type {boolean}
   * @default false
   */
  @Input('headerIsAsideEnabled')
  public isAsideEnabled: boolean = false;

  public currentUser: IUser | undefined = undefined;
  public companyLogo: string = '';
  public qimaLogo: string = IMAGES.qimaLogo;
  public isProfileMenuVisible: boolean = false;
  public isTranslateMenuVisible: boolean = false;
  public tooltipPosition: EQimaPosition.RIGHT = EQimaPosition.RIGHT;
  public tooltipMessage: string = 'APP_ASIDE.ASIDE_MENU.EXPAND_PANEL';
  public translateMenuStyle = EQimaButtonIconStyle.TERTIARY;
  public isAppcuesBannerVisible = false;

  private readonly _authenticationService = inject(AUTHENTICATION_SERVICE);

  public constructor(
    private readonly _appcuesService: AppcuesService,
    private readonly _headerService: HeaderService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _asideService: AsideService,
    private readonly _router: Router,
    public readonly qimaIcon: QimaIconService,
    public readonly qimaDivider: QimaDividerService,
    public readonly qimaButton: QimaButtonService,
    public readonly qimaTooltip: QimaTooltipService,
    public readonly qimaButtonIcon: QimaButtonIconService
  ) {}

  public ngOnInit(): void {
    this._getUserInfo();
    this.watch();
  }

  public watch(): void {
    this._authenticationService.currentUserSubject$.pipe(untilDestroyed(this)).subscribe((data: Readonly<IUser>): void => {
      this.currentUser = data;
      this._getUserInfo();
      this._changeDetectorRef.markForCheck();
    });

    this._headerService.isProfileMenuVisible$.pipe(untilDestroyed(this)).subscribe((isProfileMenuVisible: boolean): void => {
      this.isProfileMenuVisible = isProfileMenuVisible;

      this._changeDetectorRef.markForCheck();
    });

    this._headerService.isTranslateMenuVisible$.pipe(untilDestroyed(this)).subscribe((isTranslateMenuVisible: boolean): void => {
      this.isTranslateMenuVisible = isTranslateMenuVisible;
      this.translateMenuStyle = this.isTranslateMenuVisible ? EQimaButtonIconStyle.SECONDARY : EQimaButtonIconStyle.TERTIARY_UTILITY;
    });

    this._headerService.companyLogo$.pipe(untilDestroyed(this)).subscribe((logo: string): void => {
      this.companyLogo = logo;
      this._changeDetectorRef.markForCheck();
    });

    this._asideService.isAsideCollapsed$.pipe(untilDestroyed(this)).subscribe((isCollapsed: Readonly<boolean>): void => {
      this.tooltipMessage = isCollapsed ? 'APP_ASIDE.ASIDE_MENU.EXPAND_PANEL' : 'APP_ASIDE.ASIDE_MENU.COLLAPSE_PANEL';
    });

    this._watchAppcues$().pipe(untilDestroyed(this)).subscribe();
  }

  public openTranslate(event: Readonly<MouseEvent | Event>): void {
    event.stopPropagation();
    this.isTranslateMenuVisible = true;
    this.translateMenuStyle = EQimaButtonIconStyle.SECONDARY;
  }

  public onMenuClick(): void {
    this._asideService.onCollapseMenuClick();
  }

  public onQimaLogoClick(): void {
    void this._router.navigateByUrl('/home');
  }

  public onTranslateOverlayOutsideClick(): void {
    this.isTranslateMenuVisible = false;
    this.translateMenuStyle = EQimaButtonIconStyle.TERTIARY_UTILITY;
  }

  public openProfile(event: Readonly<MouseEvent | Event>): void {
    event.stopPropagation();
    this.isProfileMenuVisible = true;

    this._changeDetectorRef.markForCheck();
  }

  public onProfileOverlayOutsideClick(): void {
    this.isProfileMenuVisible = false;

    this._changeDetectorRef.markForCheck();
  }

  public onProfileOverlayDetach(): void {
    this.isProfileMenuVisible = false;

    this._changeDetectorRef.markForCheck();
  }

  public onTranslateOverlayDetach(): void {
    this.isTranslateMenuVisible = false;

    this._changeDetectorRef.markForCheck();
  }

  private _getUserInfo(): void {
    if (this._authenticationService.isAuthenticated()) {
      this.currentUser = this._authenticationService.getCurrentUser();
      this._getCompanyLogo();
    }
  }

  private _getCompanyLogo(): void {
    this._headerService
      .getCompanyLogo$()
      .pipe(untilDestroyed(this))
      .subscribe((companyLogo: QimaNullableType<string>): void => {
        if (companyLogo) {
          this.companyLogo = companyLogo;
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  private _watchAppcues$(): Observable<boolean> {
    return this._appcuesService.appcuesEvent$().pipe(
      tap({
        next: (isVisible): void => {
          this.isAppcuesBannerVisible = isVisible;
          this._changeDetectorRef.markForCheck();
        },
      })
    );
  }
}
